import { r as registerInstance, c as createEvent, g as getElement, h, H as Host } from './index-e1ee5b28.js';
import { i as trvLogoUmbrellaRed, a as trvLogo2colorBlackSmall } from './index-1074b75b.js';
import { E as EventListeners } from './EventListeners-38634b18.js';
import { o as onBreakpoint } from './onBreakpoint-9722be65.js';
import { C as Collapsible } from './Collapsible-0425bd0b.js';
import { n as normalizeKey } from './keyboard-5f746d7b.js';
import { f as hasFocus } from './helpers-01739d22.js';
import { d as doWhileEventing } from './doWhileEventing-ef9a7033.js';
import { d as devConsole } from './devConsole-1177590a.js';
import { t as translations } from './i18n-0eb3a905.js';
import { g as getToggleForGlobalHeaderDropdown, a as getNativeToggle } from './utils-a9847beb.js';
import { M as MenuToggle } from './MenuToggle-e021f8c5.js';
import './animations-bdc8e492.js';
import './customEvent-11ac0ffa.js';

const menuSlots = ['nav', 'actions', 'mobile-menu-pre', 'mobile-menu-post', 'mobile-menu-between'];
const menuSlotsSelector = menuSlots.map(s => `[slot="${s}"]`).join(',');
function bindGlobalHeader(globalHeader) {
    const { host, root } = globalHeader;
    const eventListeners = new EventListeners();
    eventListeners.addListener(host, 'click', onClick);
    eventListeners.addListener(host, 'focusout', onFocusOut);
    eventListeners.addListener(host, 'keydown', onEscape);
    if (root && root !== host) {
        eventListeners.addListener(root, 'click', onClick);
        eventListeners.addListener(root, 'focusout', onFocusOut);
        eventListeners.addListener(root, 'keydown', onEscape);
    }
    eventListeners.addListener(root || host, 'tdsExpanding', onExpandCollapse);
    eventListeners.addListener(root || host, 'tdsExpanded', onExpandCollapse);
    eventListeners.addListener(root || host, 'tdsCollapsing', onExpandCollapse);
    eventListeners.addListener(root || host, 'tdsCollapsed', onExpandCollapse);
    let offBreakpoint;
    let isMobile = false;
    let ghHeight = 0;
    setupVariantListener();
    const undoWhileEventing = doWhileEventing(window, 'resize', () => {
        onResize();
    });
    const unbind = () => {
        eventListeners.removeListeners();
        clearBreakpoint();
        undoWhileEventing();
    };
    return [unbind, onMobileBelowChange];
    function onMobileBelowChange() {
        setupVariantListener();
    }
    function setupVariantListener() {
        clearBreakpoint();
        let mobileBelow = globalHeader.config.mobileBelow;
        if (mobileBelow) {
            offBreakpoint = onBreakpoint(mobileBelow, (matches) => {
                onMobileBreakpoint(!matches);
            });
        }
    }
    function onMobileBreakpoint(mobile) {
        isMobile = mobile;
        const menuWrapper = globalHeader.menuWrapper;
        const menuToggle = globalHeader.menuToggle;
        if (menuWrapper) {
            menuWrapper.hidden = mobile;
        }
        if (menuToggle) {
            menuToggle.setAttribute('aria-expanded', 'false');
        }
        globalHeader.onMobileBreakpoint(mobile);
    }
    function clearBreakpoint() {
        if (offBreakpoint) {
            offBreakpoint();
            offBreakpoint = undefined;
        }
    }
    function onResize() {
        let centerIdentity = false;
        const idBlock = globalHeader.identityBlock;
        const sibling = idBlock && idBlock.nextElementSibling;
        if (idBlock && sibling && isMobile) {
            const idBottom = idBlock.getBoundingClientRect().bottom;
            const siblingTop = sibling.getBoundingClientRect().top;
            // trim fractions in case of retina display overlaps
            centerIdentity = Math.ceil(siblingTop) >= Math.floor(idBottom);
        }
        globalHeader.centerIdentity(centerIdentity);
        const host = globalHeader.host;
        if (globalHeader.fixed && host) {
            const height = host.offsetHeight;
            if (height !== ghHeight) {
                ghHeight = height;
                host.ownerDocument?.documentElement?.style?.setProperty('--tds-gh-fixed-height', `${height}px`);
            }
        }
    }
    function onClick(e) {
        const menuToggle = globalHeader.menuToggle;
        if (menuToggle && menuToggle.contains(e.target)) {
            toggleMenu();
        }
        else {
            const link = e.target.closest('a, tds-button[href], tds-icon-button[href]');
            if (link) {
                toggleMenu(false);
            }
        }
    }
    function onFocusOut() {
        setTimeout(() => {
            const { menuToggle, menuWrapper, host } = globalHeader;
            if (menuWrapper && menuToggle && menuToggle.getAttribute('aria-expanded') === 'true') {
                let activeElement = host.ownerDocument.activeElement;
                if (activeElement === host && host.shadowRoot) {
                    activeElement = host.shadowRoot.activeElement;
                }
                const containers = [menuToggle, menuWrapper];
                if (host.shadowRoot) {
                    host.querySelectorAll(menuSlotsSelector).forEach(el => containers.push(el));
                }
                const noFocus = !activeElement || !containers.find(el => el.contains(activeElement));
                if (noFocus) {
                    toggleMenu(false);
                }
            }
            const pairs = globalHeader.expandedPairs;
            pairs.forEach(({ toggle, toggled }) => {
                if (!hasFocus(toggle) && !hasFocus(toggled)) {
                    toggle.click();
                }
            });
        });
    }
    function onEscape(e) {
        if (normalizeKey(e) === 'Escape') {
            const target = e.target;
            const menuWrapper = globalHeader.menuWrapper;
            const menuToggle = globalHeader.menuToggle;
            const containers = [menuToggle, menuWrapper];
            if (host.shadowRoot) {
                host.querySelectorAll(menuSlotsSelector).forEach(el => containers.push(el));
            }
            const targetContainer = containers.find(el => el && el.contains(target));
            if (targetContainer) {
                toggleMenu(false);
                e.preventDefault();
                e.stopPropagation();
                if (menuToggle) {
                    menuToggle.focus();
                }
            }
        }
    }
    function onExpandCollapse(e) {
        const target = e.target;
        const menuWrapper = globalHeader.menuWrapper;
        if (target === menuWrapper) {
            // See mixin global-header-menu-wrapper-mobile in ./mixins/_globalHeader.scss
            // for explanation of why we add 'tds-expanding' here.
            menuWrapper.classList.toggle('tds-expanding', e.type === 'tdsExpanding');
            menuWrapper.classList.toggle('tds-collapsing', e.type === 'tdsCollapsing');
        }
    }
    function toggleMenu(expand) {
        const menuToggle = globalHeader.menuToggle;
        const menuWrapper = globalHeader.menuWrapper;
        if (isMobile && menuWrapper && (typeof expand === 'undefined' || expand === menuWrapper.hidden)) {
            new Collapsible(menuWrapper).toggle(menuToggle);
        }
    }
}

const globalHeaderCss = ".sr-only{border:0;clip:rect(0, 0, 0, 0);-webkit-clip-path:inset(50%);clip-path:inset(50%);height:1px;overflow:hidden;padding:0;position:absolute;text-transform:none;white-space:nowrap;width:1px}.wrapper{margin-left:auto;margin-right:auto;max-width:var(--tds-container-max-width, 103rem);padding-left:var(--tds-container-margin, 1.125rem);padding-right:var(--tds-container-margin, 1.125rem)}*,*::before,*::after{box-sizing:border-box}.menu-toggle{border:0;border-radius:0.1875rem;background-color:var(--tds-nav-background-color, rgb(255, 255, 255));--menu-toggle-color:var(--tds-nav-text-color, var(--tds-text-base-color, rgb(39, 42, 45)));color:var(--menu-toggle-color);cursor:pointer;display:inline-block;height:2.75rem;margin-left:auto;font-size:0.75rem;line-height:1.5;font-weight:600;padding:0 0.375rem}.menu-toggle:focus-visible{outline:solid 0.25rem var(--tds-focus-ring-color, rgb(0, 104, 169));outline-offset:var(--tds-focus-ring-offset, 0)}.menu-toggle:hover{background-color:var(--tds-nav-background-hover-color, rgb(246, 246, 246))}.menu-toggle:active{background-color:var(--tds-nav-background-active-color, rgb(246, 246, 246));--menu-toggle-color:var(--tds-nav-active-color, var(--tds-brand-primary-dark1, rgb(175, 18, 20)));--icon-color-delay:0}.menu-toggle .menu-toggle-text-expanded{display:none}.menu-toggle[aria-expanded=true] .menu-toggle-text-expanded{display:unset}.menu-toggle[aria-expanded=true] .menu-toggle-text{display:none}.menu-toggle-icon{display:block;position:relative;margin:0.5rem auto;width:2rem;height:0.125rem;border-radius:1px;background-color:var(--menu-toggle-color);transition:all 0s var(--icon-color-delay, 0.25s) linear}.menu-toggle-icon::before,.menu-toggle-icon::after{content:\"\";position:absolute;left:0;right:0;height:inherit;background-color:var(--menu-toggle-color);border-radius:inherit;transition:top, transform, left, right ease-in-out;transition-duration:0.25s;transition-delay:0.25s, 0s, 0s, 0s}.menu-toggle-icon::before{top:-0.5rem}.menu-toggle-icon::after{top:0.5rem}[aria-expanded=true] .menu-toggle-icon{background-color:transparent}[aria-expanded=true] .menu-toggle-icon::before,[aria-expanded=true] .menu-toggle-icon::after{top:0;left:2px;right:2px;transition-delay:0s, 0.25s, 0.25s, 0.25s}[aria-expanded=true] .menu-toggle-icon::before{transform:rotate(45deg)}[aria-expanded=true] .menu-toggle-icon::after{transform:rotate(-45deg)}:host{display:block;background-color:rgb(255, 255, 255);border-bottom:solid 0.0625rem rgb(218, 219, 219);position:relative;display:flex;flex-direction:column;align-items:center;background-color:rgb(255, 255, 255)}:host(.tds-gh-fixed){position:fixed;top:0;left:0;right:0;z-index:1030}:host(.tds-gh-sticky){position:sticky;padding-top:1px;margin-top:-1px;top:-1px;z-index:1030}.wrapper{align-items:flex-end;display:flex;flex-wrap:wrap;row-gap:0.5rem;justify-content:center;margin-top:1.875rem;min-height:2.75rem;width:100%}.identity-block{color:inherit;display:flex;flex-direction:row;flex:999 0 auto;outline:0}.identity-block.centered{justify-content:center}.identity{background-color:rgba(0, 0, 0, 0);display:flex;align-items:center;flex-wrap:nowrap;white-space:nowrap;margin-bottom:6px;min-height:2rem;-webkit-text-decoration:none;text-decoration:none}.identity:hover{background-color:rgba(0, 0, 0, 0);border:0;color:inherit}.identity:visited{color:inherit}.identity:focus-visible{border-radius:0.1875rem;outline:solid 0.25rem var(--tds-focus-ring-color, rgb(0, 104, 169));outline-offset:var(--tds-focus-ring-offset, 0.125rem)}.logo{color:rgb(0, 0, 0);display:block;height:var(--tds-global-header-logo-height, 1.5rem);width:var(--tds-global-header-logo-width, 7.375rem)}.product{font-family:BattersonSans, sans-serif;font-weight:600;font-style:normal;border-left:0.0625rem solid rgb(107, 109, 113);color:rgb(70, 73, 77);font-size:var(--tds-global-header-product-font-size, 0.9375rem);letter-spacing:0.0375rem;line-height:1.75rem;margin:0 0 0 0.375rem;padding:0 0 0 0.375rem}.identity.umbrella-only{--tds-global-header-logo-width:1.6875rem}.identity.umbrella-only .product{font-size:inherit}.menu-wrapper{display:flex;flex-direction:row;flex-wrap:nowrap;justify-content:space-between;flex:1 0 auto}.menu-wrapper[hidden]{display:none}.actions-block{--tds-icon-button-height:2.75rem;display:flex;flex-direction:row;flex-wrap:nowrap;align-items:center;margin-left:auto;min-height:2.75rem}.actions-block ::slotted(tds-icon-button),.mobile-block ::slotted(tds-icon-button){position:relative;color:var(--tds-nav-text-color, var(--tds-text-base-color, rgb(39, 42, 45)))}.actions-block ::slotted(tds-icon-button)::after,.mobile-block ::slotted(tds-icon-button)::after{content:\"\";border-bottom:solid 0.125rem;height:0.125rem;width:100%;position:absolute;bottom:0;left:0;opacity:0;transition-duration:0.25s;transition-property:background-color, color;transition-timing-function:ease-in-out}.actions-block ::slotted(tds-icon-button:hover),.mobile-block ::slotted(tds-icon-button:hover){background-color:var(--tds-nav-background-hover-color, rgb(246, 246, 246))}.actions-block ::slotted(tds-icon-button:hover)::after,.mobile-block ::slotted(tds-icon-button:hover)::after{opacity:1}.actions-block ::slotted(tds-icon-button:active),.actions-block ::slotted(tds-icon-button.active),.mobile-block ::slotted(tds-icon-button:active),.mobile-block ::slotted(tds-icon-button.active){background-color:var(--tds-nav-background-active-color, rgb(246, 246, 246));color:var(--tds-nav-active-color, var(--tds-brand-primary-dark1, rgb(175, 18, 20)))}.actions-block ::slotted(tds-icon-button:active)::after,.actions-block ::slotted(tds-icon-button.active)::after,.mobile-block ::slotted(tds-icon-button:active)::after,.mobile-block ::slotted(tds-icon-button.active)::after{opacity:1}.actions-block ::slotted(tds-icon-button[current]:not([current=false]):not(:active)),.mobile-block ::slotted(tds-icon-button[current]:not([current=false]):not(:active)){color:var(--tds-nav-current-color, var(--tds-brand-primary, rgb(224, 23, 25)))}.actions-block ::slotted(tds-icon-button[current]:not([current=false]):not(:active))::after,.mobile-block ::slotted(tds-icon-button[current]:not([current=false]):not(:active))::after{opacity:1}.actions-block ::slotted(tds-button),.mobile-block ::slotted(tds-button){margin-left:0.375rem}.mobile-menu-block{display:none}.top-hat{width:100%;margin-bottom:-1.3125rem;order:-1}.top-hat.hide{display:none}.mobile-block{display:none}:host(.__gh-mobile) .wrapper{padding-bottom:0.375rem}:host(.__gh-mobile) .mobile-block{--tds-icon-button-height:2.75rem;display:flex;flex-direction:row;flex-wrap:nowrap;align-items:center;margin-left:auto;min-height:2.75rem;flex:1 0 auto}:host(.__gh-mobile) .menu-wrapper{flex-direction:column;position:absolute;background-color:var(--tds-nav-background-color, rgb(255, 255, 255));border-radius:0.1875rem;box-shadow:0 0.0625rem 0.125rem 0 rgba(51, 93, 121, 0.07), 0 0.125rem 0.25rem 0 rgba(51, 93, 121, 0.07), 0 0.25rem 0.5rem 0 rgba(51, 93, 121, 0.07), 0 0.5rem 1rem 0 rgba(51, 93, 121, 0.07), 0 1rem 2rem 0 rgba(51, 93, 121, 0.07);min-width:20rem;margin-top:0.0625rem;max-width:100%;width:max-content;top:100%;z-index:1060;right:0;max-height:calc(100vh - (100% + 0.0625rem))}:host(.__gh-mobile) .menu-wrapper:not(.tds-expanding) .nav-block:not(:last-child){flex-shrink:1;overflow-y:auto}:host(.__gh-mobile) .menu-wrapper.tds-collapsing .nav-block:not(:last-child){overflow-y:hidden}:host(.__gh-mobile) .menu-wrapper>*:nth-child(n+2){border-top:solid 0.0625rem rgb(218, 219, 219)}:host(.__gh-mobile) .actions-block{border-top:solid 0.0625rem rgb(218, 219, 219);padding:0 0.75rem;width:100%;display:grid;grid-template-columns:repeat(99, max-content);grid-template-rows:2.75rem auto}:host(.__gh-mobile) .mobile-menu-block{display:block;padding:0.75rem}:host(:not(.__gh-mobile)) ::slotted(.tds-global-header__mobile-only),:host(.__gh-mobile) ::slotted(.tds-global-header__hide-mobile){display:none}::slotted(:not(.tds-no-logo)[slot=identity]){display:block !important;padding:0.5rem 0 0.5rem 8.1875rem !important;margin:0 !important;color:rgb(70, 73, 77) !important;-webkit-text-decoration:none !important;text-decoration:none !important;line-height:1.75rem !important;font-size:0.9375rem !important;font-family:BattersonSans, sans-serif;font-weight:600;font-style:normal;position:relative;white-space:nowrap}::slotted(:not(.tds-no-logo)[slot=identity])::before,::slotted(:not(.tds-no-logo)[slot=identity])::after{content:\"\";position:absolute}::slotted(:not(.tds-no-logo)[slot=identity])::before{width:7.375rem;height:1.5rem;background-image:url(\"data:image/svg+xml,%3Csvg%20width%3D%22148%22%20height%3D%2230%22%20viewBox%3D%220%200%20148%2030%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cpath%20d%3D%22M116.592%2013.025c1.83%200%203.834.25%204.557.453l-.35%202.688c-.865-.138-3.018-.41-4.53-.41-1.671%200-2.237.431-2.384%201.764-.173%201.558.124%201.717%202.374%202.439%203.548%201.173%204.66%201.784%204.277%205.238-.397%203.612-2.077%204.743-6.371%204.743-2.235%200-3.934-.317-4.707-.477l.434-2.709c.486.11%203.074.495%204.614.495%202.076%200%202.66-.36%202.84-2.033.171-1.532-.077-1.734-2.41-2.48-3.706-1.195-4.542-1.805-4.166-5.24.362-3.297%202.073-4.47%205.822-4.47zm-71.074.205l2.25%2012.076%204.75-12.076h3.14l-6.866%2016.616h-3.184L42.267%2013.23h3.251zm57.824%200c3.589%200%205.306.857%204.84%205.059-.311%202.868-1.427%204.018-3.41%204.58l2.138%206.886h-3.12l-2-6.486h-2.533l-.717%206.486h-3.073l1.818-16.525h6.057zm-9.476%200l-.303%202.755H86.96l-.434%203.928h5.354l-.306%202.79h-5.349l-.478%204.288h6.815l-.298%202.764h-9.958L84.12%2013.23h9.745zm-19.097%200L73.254%2026.99h7.286c-.387%201.75-1.149%202.764-2.91%202.764h-7.818L71.63%2013.23h3.138zm-6.541%200l-.305%202.755h-6.602l-.432%203.928h5.355l-.31%202.79h-5.35l-.476%204.288h6.817l-.3%202.764h-9.96l1.82-16.525h9.743zm-30.572-.09l3.271%2016.615H37.7l-.512-2.753h-5.373l-1.141%202.753h-3.141l7.106-16.616h3.017zm-15.44.09c3.592%200%205.308.857%204.844%205.059-.315%202.868-1.429%204.018-3.41%204.58l2.137%206.886h-3.095l-2-6.486h-2.559l-.715%206.486h-3.072l1.817-16.525h6.053zm-8.432%200c-.386%201.748-1.149%202.765-2.908%202.765H8.282l-1.49%2013.76H3.627l1.514-13.77H0l.305-2.755zm21.83%204.239l-2.722%206.938h3.813l-1.091-6.938zm67.186-1.617h-2.73l-.526%204.796h2.798c1.448%200%202.435-.054%202.702-2.473.268-2.439-.844-2.323-2.244-2.323zm-81.074%200h-2.78l-.532%204.796h2.854c1.447%200%202.435-.054%202.7-2.473.269-2.439-.892-2.323-2.242-2.323z%22%2F%3E%3Cpath%20d%3D%22M132.006%2011.343v-.017a.088.088%200%2000-.079-.087l-.004-.001a7.852%207.852%200%2000-1.679-.016l-.007.001a.087.087%200%2000-.078.088l-.001.004.002%2015.1a1.56%201.56%200%2001-3.028.52l-.002-.005a.925.925%200%2000-1.765.542l.002.007a3.41%203.41%200%20003.234%202.34%203.41%203.41%200%20003.405-3.404V11.343z%22%20fill%3D%22%23E21B23%22%2F%3E%3Cpath%20d%3D%22M114.266%2011.201a.104.104%200%2001-.152-.131l.005-.008C116.978%204.549%20123.484%200%20131.053%200c7.571%200%2014.08%204.553%2016.935%2011.072l.002.007c.006.012.01.026.01.04a.104.104%200%2001-.104.105.117.117%200%2001-.049-.013l-.014-.005c-1.311-.774-3.143-1.252-5.17-1.252-2.34%200-4.419.639-5.738%201.63l-.006.005a.104.104%200%2001-.127%200l-.006-.004c-1.322-.992-3.401-1.631-5.741-1.631s-4.419.639-5.74%201.63l-.005.004a.106.106%200%2001-.123.006l-.009-.008c-1.32-.992-3.402-1.632-5.742-1.632-2.017%200-3.84.473-5.15%201.24l-.01.007z%22%20fill%3D%22%23E21B23%22%2F%3E%3C%2Fsvg%3E\");background-repeat:no-repeat;background-size:100%;left:0;top:50%;transform:translateY(-50%)}::slotted(:not(.tds-no-logo)[slot=identity])::after{height:1.75rem;border-left:solid 1px rgb(107, 109, 113);left:7.75rem;top:50%;transform:translateY(-50%)}::slotted(.tds-umbrella-only[slot=identity]){padding-left:2.5rem !important;font-size:inherit !important}::slotted(.tds-umbrella-only[slot=identity])::before{width:1.6875rem;background-image:url(\"data:image/svg+xml,%3Csvg%20width%3D%2253%22%20height%3D%2248%22%20viewBox%3D%220%200%2053%2048%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cpath%20d%3D%22M28.235%2017.88v23.089a5.223%205.223%200%2001-5.217%205.215%205.224%205.224%200%2001-4.955-3.585l-.003-.01a1.414%201.414%200%20011.362-1.794c.626%200%201.155.405%201.343.963l.003.007a2.391%202.391%200%20002.25%201.589%202.39%202.39%200%20002.388-2.385l-.003-23.133.001-.006c0-.07.051-.128.12-.135l.01-.002a12.03%2012.03%200%20012.572.024l.007.002c.066.006.12.064.12.133l.002.027zM1.056%2017.66a.16.16%200%2001-.232-.201l.007-.012C5.211%207.47%2015.18.501%2026.774.501c11.6%200%2021.57%206.975%2025.945%2016.962l.003.01a.15.15%200%2001.015.064.16.16%200%2001-.159.159.18.18%200%2001-.075-.02l-.022-.007c-2.009-1.185-4.815-1.919-7.92-1.919-3.584%200-6.77.98-8.79%202.498l-.01.007a.16.16%200%2001-.194%200l-.01-.006c-2.024-1.52-5.21-2.499-8.795-2.499-3.584%200-6.77.98-8.793%202.5l-.008.004a.163.163%200%2001-.189.009l-.013-.012c-2.022-1.52-5.213-2.501-8.798-2.501-3.09%200-5.881.726-7.888%201.9l-.017.011z%22%20fill%3D%22%23F50002%22%2F%3E%3C%2Fsvg%3E\")}::slotted(.tds-umbrella-only[slot=identity])::after{left:2.0625rem}";
const TdsGlobalHeaderStyle0 = globalHeaderCss;

const GlobalHeader = class {
    onMobileBelowChange() {
        const internals = this.__internals;
        if (internals) {
            internals.mobileBelowChanged();
        }
    }
    constructor(hostRef) {
        registerInstance(this, hostRef);
        this.tdsMobileBreakpoint = createEvent(this, "tdsMobileBreakpoint", 7);
        this.tdsIdentityClicked = createEvent(this, "tdsIdentityClicked", 7);
        this.homehref = undefined;
        this.homelabel = undefined;
        this.logotext = undefined;
        this.hometext = undefined;
        this.product = undefined;
        this.mobileBelow = undefined;
        this.menuToggleLabel = undefined;
        this.menuToggleLabelExpanded = undefined;
        this.umbrellaOnly = undefined;
        this.tophatAlways = undefined;
        this.fixed = undefined;
        this.sticky = undefined;
        this.maxWidth = undefined;
        this.isMobile = undefined;
        this.centerIdentity = undefined;
        this.__internals = new GlobalHeaderInternals(this);
    }
    connectedCallback() {
        if (this.__internals.loaded) {
            this.__internals.setup();
        }
    }
    componentDidLoad() {
        const internals = this.__internals;
        internals.loadTimeout = setTimeout(() => {
            internals.loadTimeout = undefined;
            internals.autoConfigure();
            internals.setup();
            internals.loaded = true;
        });
    }
    disconnectedCallback() {
        const internals = this.__internals;
        const loadTimeout = internals.loadTimeout;
        if (loadTimeout) {
            clearTimeout(loadTimeout);
            internals.loadTimeout = undefined;
        }
        else {
            this.__internals?.teardown();
        }
    }
    render() {
        return this.__internals.render();
    }
    get host() { return getElement(this); }
    static get watchers() { return {
        "mobileBelow": ["onMobileBelowChange"]
    }; }
};
class GlobalHeaderInternals {
    get identityBlock() {
        return this.root?.querySelector('div.identity-block');
    }
    get menuWrapper() {
        return this.root?.querySelector('div.menu-wrapper');
    }
    get wrapper() {
        return this.root?.querySelector('div.wrapper');
    }
    get fixed() {
        return !!this.host.fixed;
    }
    constructor(instance) {
        this.loaded = false;
        this.instance = instance;
        this.host = instance.host;
        this.root = this.host?.shadowRoot || this.host;
        this.onIdentifyClick = this.onIdentifyClick.bind(this);
    }
    get expandedPairs() {
        const host = this.host;
        const dropdowns = host ? Array.from(host.querySelectorAll('tds-global-header-dropdown')) : [];
        return dropdowns.map(dd => {
            let toggleEl = getToggleForGlobalHeaderDropdown(dd);
            if (toggleEl) {
                toggleEl = getNativeToggle(toggleEl);
            }
            return { toggle: toggleEl, toggled: dd };
        }).filter(({ toggle }) => toggle && toggle.getAttribute('aria-expanded') === 'true');
    }
    get config() {
        return this.instance;
    }
    setup() {
        const menuWrapper = this.menuWrapper;
        // only bind when there is a menuWrapper. Otherwise, unnecessary click handlers are added 
        // to global header, which makes NVDA announce the header as 'clickable' when it isn't
        // NOTE: This logic does not account for the possibility of menuWrapper added after initial
        // rendering.
        if (menuWrapper) {
            const [unbind, onMobileBelowChange] = bindGlobalHeader(this);
            this.unbind = unbind;
            this.onMobileBelowChange = onMobileBelowChange;
        }
    }
    teardown() {
        const unbind = this.unbind;
        if (unbind) {
            unbind();
            delete this.unbind;
            delete this.onMobileBelowChange;
        }
    }
    checkHideMenuWrapper(menuWrapper) {
        if (!this.loaded) {
            const { instance } = this;
            if (instance.mobileBelow && menuWrapper) {
                // this is to prevent a flash of the nav when first loading
                // setup will update this as needed 
                menuWrapper.hidden = true;
            }
        }
    }
    onMobileBreakpoint(mobile) {
        this.instance.isMobile = mobile;
        const host = this.host;
        if (host) {
            host.querySelectorAll('tds-global-header-dropdown[slot="actions"]')
                .forEach(dd => {
                if (dd.parentElement === host) {
                    dd.classList.toggle('__gh-mobile', mobile);
                }
            });
            this.instance?.tdsMobileBreakpoint.emit({ mobile });
        }
    }
    mobileBelowChanged() {
        const { mobileBelow, host } = this.instance;
        const onMobileBelowChange = this.onMobileBelowChange;
        const navEl = host.querySelector('tds-nav');
        const tdsNav = navEl && navEl.parentElement === this.host ? navEl : null;
        if (tdsNav && tdsNav.variantAt !== mobileBelow) {
            tdsNav.variantAt = mobileBelow;
        }
        onMobileBelowChange && onMobileBelowChange();
    }
    centerIdentity(center) {
        this.instance.centerIdentity = center;
    }
    onIdentifyClick(event) {
        const instance = this.instance;
        if (instance && instance.tdsIdentityClicked.emit().defaultPrevented) {
            event.preventDefault();
        }
    }
    autoConfigure() {
        const { mobileBelow, host } = this.instance;
        const navEl = host.querySelector('tds-nav');
        const tdsNav = navEl && navEl.parentElement === this.host ? navEl : null;
        const variantAt = tdsNav && tdsNav.variantAt;
        const variant = tdsNav && tdsNav.variant;
        if (mobileBelow) {
            if (tdsNav) {
                if (variantAt && variantAt !== mobileBelow) {
                    devConsole.warn(`tds-nav "variantAt" property (${variantAt}) is different than the global header's "mobileBelow" property (${mobileBelow}). tds-nav will be updated.`);
                }
                if (variant && variant !== 'row') {
                    devConsole.warn(`tds-nav "variant" property (${variant}) should be set to "row" when in header. tds-nav will be updated.`);
                }
                tdsNav.variantAt = mobileBelow;
                tdsNav.variant = 'row';
            }
        }
        else if (tdsNav) {
            if (variant !== 'row') {
                if (variant) {
                    devConsole.warn(`tds-nav \`variant\` property should be set to "row" when in header. tds-nav will be updated.`);
                }
                tdsNav.variant = 'row';
            }
            if (variantAt) {
                if (tdsNav.menuToggle) {
                    // nav manages menu toggle. ensure it is set correctly
                    if (tdsNav.menuOpenBehavior === 'push-down') {
                        tdsNav.menuOpenBehavior = 'dropdown-full';
                    }
                }
                else {
                    host.mobileBelow = variantAt;
                }
            }
        }
    }
    render() {
        const { host, tophatAlways, fixed, sticky, centerIdentity, isMobile, mobileBelow } = this.instance;
        const TOP_HAT_SLOT_NAME = 'top-hat';
        const hasTopSlot = !!host.querySelector(`[slot="${TOP_HAT_SLOT_NAME}"]`);
        const hTop = hasTopSlot ? h("div", { class: { 'top-hat': true, hide: isMobile && !tophatAlways } }, h("slot", { name: TOP_HAT_SLOT_NAME })) : '';
        const bannerDescendent = host.parentElement?.closest('header, [role="banner"]');
        const role = bannerDescendent ? undefined : host.getAttribute('role') || 'banner';
        this.translate = translations(this.host);
        const classes = {
            '__gh-mobile': isMobile,
            'tds-gh-fixed': fixed,
            'tds-gh-sticky': sticky
        };
        const identity = this.renderIdentity();
        const menuWrapper = this.renderMenuWrapper();
        const mobileBlock = !!menuWrapper && !!mobileBelow && this.renderMobileBlock();
        return (h(Host, { class: classes, role: role }, h("div", { class: "wrapper" }, h("div", { class: { "identity-block": true, centered: centerIdentity } }, identity), mobileBlock, menuWrapper), hTop));
    }
    renderIdentity() {
        const IDENTITY_SLOT_NAME = "identity";
        const LOGO_SLOT_NAME = "logo";
        const PRODUCT_SLOT_NAME = "product";
        const { host, homehref, homelabel, hometext, logotext, product, umbrellaOnly } = this.instance;
        if (!!host.querySelector(`[slot="${IDENTITY_SLOT_NAME}"]`)) {
            return h("slot", { name: IDENTITY_SLOT_NAME });
        }
        const hasProductSlot = !!host.querySelector(`[slot="${PRODUCT_SLOT_NAME}"]`);
        const hasProduct = hasProductSlot || !!product;
        const hasLogoSlot = !!host.querySelector(`[slot="${LOGO_SLOT_NAME}"]`);
        const logoAttr = {
            class: "logo",
            role: "img",
            'aria-hidden': 'true',
            focusable: 'false'
        };
        const useLogoText = logotext ?? (hasLogoSlot ? undefined : 'Travelers');
        const useHomeText = homehref && (hometext ?? this.translate.html('home'));
        const logoSVG = umbrellaOnly ? trvLogoUmbrellaRed : trvLogo2colorBlackSmall;
        const hLogo = hasLogoSlot ? h("slot", { name: LOGO_SLOT_NAME }) :
            h("span", { innerHTML: logoSVG.svg(logoAttr) });
        const hProduct = hasProduct ?
            h("span", { class: "product" }, hasProductSlot ? h("slot", { name: PRODUCT_SLOT_NAME }) : product)
            : '';
        const TagName = homehref ? 'a' : 'div';
        const classes = {
            'identity': true,
            'umbrella-only': umbrellaOnly
        };
        //todo: assert that href and label are provided when required?
        return (h(TagName, { class: classes, href: homehref, "aria-label": homehref ? homelabel : undefined, onClick: homehref ? this.onIdentifyClick : undefined }, hLogo, useLogoText && h("span", { class: "sr-only" }, useLogoText), hProduct, useHomeText && h("span", { class: "sr-only" }, useHomeText)));
    }
    renderMenuWrapper() {
        const navBlock = this.renderBlock('nav', 'nav-block');
        const actionsBlock = this.renderBlock('actions', 'actions-block');
        return (navBlock || actionsBlock) ? (h("div", { key: "mobile-wrapper", class: "menu-wrapper", ref: el => this.checkHideMenuWrapper(el) }, this.renderBlock('mobile-menu-pre', 'mobile-menu-block'), navBlock, this.renderBlock('mobile-menu-between', 'mobile-menu-block'), actionsBlock, this.renderBlock('mobile-menu-post', 'mobile-menu-block'))) : '';
    }
    renderMobileBlock() {
        const instance = this.instance;
        return (h("div", { key: "mobile-block", class: "mobile-block" }, h("slot", { name: "mobile-actions" }), h(MenuToggle, { translations: this.translate, label: instance.menuToggleLabel, labelExpanded: instance.menuToggleLabelExpanded, ref: el => this.menuToggle = el })));
    }
    renderBlock(slotName, cls) {
        let selector = `[slot="${slotName}"]`;
        if (slotName === 'actions' && this.instance.isMobile) {
            // if all action slots have class tds-global-header__hide-mobile, do not render mobile
            selector += ':not(.tds-global-header__hide-mobile)';
        }
        return this.host.querySelector(selector) ?
            h("div", { class: cls }, h("slot", { name: slotName })) : '';
    }
}
GlobalHeader.style = TdsGlobalHeaderStyle0;

export { GlobalHeader as tds_global_header };

//# sourceMappingURL=tds-global-header.entry.js.map